import {ActionIcon, Group, Select, Tooltip} from '@mantine/core';
import React from 'react';

import {FieldMigrationMapping} from 'antibodies/schema-configuration/schema-migration/AntibodySchemaMigrationConstants';
import type {useStyles} from 'antibodies/schema-configuration/schema-migration/NewFieldsMigrationPreview';
import {ClientSchemaField} from 'registry/field/FieldTable';
import getIconAndLabel from 'registry/field/GetFieldLabelAndIcon';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

type MigrationClasses = ReturnType<typeof useStyles>['classes'];

function getFieldTypeLabel(
  field: ClientSchemaField,
  schemaFieldSelectors: Array<SchemaFieldSelector>,
  classes: MigrationClasses
): JSX.Element {
  const {icon, label} = getIconAndLabel({
    field,
    schemaFieldSelectors,
    inEditMode: false,
  });
  return (
    <Group spacing="xs" align="center" noWrap={true}>
      <BenchlingIcon icon={icon} className={classes.iconStyle} />
      {label}
    </Group>
  );
}

export interface NewFieldMigrationRowProps {
  destField: ClientSchemaField;
  allowedOptions: Array<string>;
  isMigratable: boolean;
  currentMapping: FieldMigrationMapping | undefined;
  matchingFields: Array<SchemaField | ClientSchemaField>;
  hasUnmapped: boolean;
  showSourceColumn: boolean;
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  classes: MigrationClasses;
  onSourceFieldChange: (sourceFieldId: string | null) => void;
  onOpenOptionMapping: () => void;
}

export default function NewFieldMigrationRow({
  destField,
  allowedOptions,
  isMigratable,
  currentMapping,
  matchingFields,
  hasUnmapped,
  showSourceColumn,
  schemaFieldSelectors,
  classes,
  onSourceFieldChange,
  onOpenOptionMapping,
}: NewFieldMigrationRowProps): JSX.Element {
  const sourceFieldId = currentMapping?.sourceFieldId ?? null;
  // Only fields with an apiId can be a Select option (the value is the apiId), so drop any without
  // one rather than asserting; disabled then reflects whether any selectable source actually remains.
  const sourceFieldOptions = matchingFields
    .filter((f): f is typeof f & {apiId: string} => f.apiId != null)
    .map((f) => ({value: f.apiId, label: f.displayName}));

  return (
    <tr>
      <td>{destField.name}</td>
      <td className={classes.definitionCell}>
        {getFieldTypeLabel(destField, schemaFieldSelectors, classes)}
      </td>
      <td>{destField.isRequired ? 'Required' : ''}</td>
      {showSourceColumn && (
        <td>
          {isMigratable && (
            <Group spacing="xs" noWrap={true}>
              <Select
                aria-label={`Source field for ${destField.name}`}
                data={sourceFieldOptions}
                value={sourceFieldId}
                onChange={(val) => {
                  onSourceFieldChange(val);
                }}
                clearable={true}
                placeholder="None"
                disabled={sourceFieldOptions.length === 0}
              />
              {sourceFieldId != null && allowedOptions.length > 0 && (
                <Tooltip
                  label={
                    hasUnmapped
                      ? 'Some source options aren’t mapped yet. Choose a destination option for each, ' +
                        'or “— none —” to clear it.'
                      : 'Map options'
                  }
                  multiline={hasUnmapped}
                  width={hasUnmapped ? 240 : undefined}
                  withArrow={true}
                >
                  <Group spacing="xs" noWrap={true}>
                    <ActionIcon
                      size="sm"
                      aria-label={`Map options for ${destField.name}`}
                      variant="default"
                      onClick={() => {
                        onOpenOptionMapping();
                      }}
                    >
                      <BenchlingIcon icon="chevron-right" className={classes.iconStyle} />
                    </ActionIcon>
                    {hasUnmapped && <BenchlingIcon icon="warning" className={classes.warningIcon} />}
                  </Group>
                </Tooltip>
              )}
            </Group>
          )}
        </td>
      )}
    </tr>
  );
}
